const loadColumnWidths = (storageKey) => {
    try {
        const raw = localStorage.getItem(storageKey);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

const saveColumnWidths = (storageKey, widths) => {
    try {
        localStorage.setItem(storageKey, JSON.stringify(widths));
    } catch {
        console.warn('Could not save columnWidths');
    }
};

const getClientX = (event) => {
    if (typeof event.clientX === 'number') return event.clientX;
    return event.touches?.at(0)?.clientX || 0;
};

const getClosestCell = ({ target }) => target.closest?.('th, td');

const suppressClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    document.removeEventListener('click', suppressClick, true);
};

export const ResizableTable = ({
    id,
    columnIds,
    tableEl,
    wrapperEl,
    resizeCursor,
    handleWidth,
    minColumnWidth,
    onColumnsUpdate,
    onCursorChange,
    onColumnResize,
}) => {
    const storageKey = `resizable-table-v1-${id}`;
    let activeCol = null;
    let activeColSibling = null;
    let hoverResizeActive = false;
    let startX = 0;
    let startWidth = 0;
    let startWidthSibling = 0;
    let currentWidth = 0;
    let currentWidthSibling = 0;
    let pointerId = null;
    let startScrollLeft = 0;
    let otherColumnsWidth = 0;

    const calculateEqualColumnWidths = (ids) => {
        const savedColumnWidths = Object.fromEntries(
            Object.entries(loadColumnWidths(storageKey) || {}).filter(([id]) => ids.includes(id))
        );
        const savedWidthsSum = Object.values(savedColumnWidths).reduce((acc, n) => acc + n, 0);
        const notSavedColumnIds = ids.filter((id) => !Object.keys(savedColumnWidths).includes(id));
        const wrapperWidth = wrapperEl.getBoundingClientRect().width - 2;
        const otherHeaders = [...tableEl.querySelectorAll('thead th')].filter(({ dataset }) => !dataset.id);
        const otherWidthSum = otherHeaders.reduce((acc, th) => acc + th.getBoundingClientRect().width, 0) + savedWidthsSum;
        const lastColumnId = ids[ids.length - 1];

        if (notSavedColumnIds.length === 0) {
            savedColumnWidths[lastColumnId] += Math.max(0, wrapperWidth - otherWidthSum);
            return savedColumnWidths;
        }

        const totalWidthSpace = wrapperWidth - otherWidthSum;
        const equalColumnWidth = Math.max(minColumnWidth, Math.floor(totalWidthSpace / notSavedColumnIds.length));

        const result = {
            ...notSavedColumnIds
                .reduce((acc, id) => ({ ...acc, [id]: equalColumnWidth }), {}),
            ...savedColumnWidths,
        };

        result[lastColumnId] += Math.max(0, wrapperWidth - (otherWidthSum + notSavedColumnIds.length * equalColumnWidth));
        return result;
    };

    let calculatedWidths = calculateEqualColumnWidths(columnIds);
    onColumnsUpdate(calculatedWidths);

    const setDocumentCursor = (setResizeCursor, colId) => {
        const bodyStyle = document.body.style;
        if (setResizeCursor) {
            bodyStyle.cursor = resizeCursor;
            bodyStyle.userSelect = 'none';
        } else {
            bodyStyle.cursor = '';
            bodyStyle.userSelect = '';
        }
        onCursorChange(setResizeCursor, colId);
    };

    const getHeaderForCell = (cell, usePrev) => {
        if (!cell) return null;
        const cellIndex = [...cell.parentNode.children].indexOf(cell);
        const th = tableEl.querySelector(`thead th:nth-child(${cellIndex + (usePrev ? 0 : 1)})`);
        return th?.dataset.id ? th : null;
    };

    const getHandlePosition = (e) => {
        const cell = getClosestCell(e);

        if (!cell) return { nearLeft: false, nearRight: false, cell };

        const rect = cell.getBoundingClientRect();
        const cx = getClientX(e);

        return {
            nearRight: Math.abs(cx - rect.right) <= (handleWidth / 2),
            nearLeft: Math.abs(cx - rect.left) <= (handleWidth / 2),
            cell,
        };
    };

    const getHandleStatus = (e) => {
        const { nearLeft, nearRight, cell } = getHandlePosition(e);
        const header = getHeaderForCell(cell, nearLeft);
        return {
            header,
            isOnHandle: header && (nearLeft || nearRight)
        };
    };

    const attachResizePointerListeners = () => {
        document.addEventListener('pointermove', onPointerMove, { passive: true });
        document.addEventListener('pointerup', onPointerUp);
        document.addEventListener('pointercancel', onPointerUp);
    };

    const detachResizePointerListeners = () => {
        document.removeEventListener('pointermove', onPointerMove, { passive: true });
        document.removeEventListener('pointerup', onPointerUp);
        document.removeEventListener('pointercancel', onPointerUp);
    };

    const onPointerHover = (e) => {
        if (activeCol) return;
        if (e.pointerType === 'touch') return;

        const cell = getClosestCell(e);
        if (!cell) {
            if (hoverResizeActive) {
                hoverResizeActive = false;
                setDocumentCursor(false);
            }
            return;
        }

        const { header, isOnHandle } = getHandleStatus(e);

        if (isOnHandle) {
            if (!hoverResizeActive) {
                hoverResizeActive = true;
                setDocumentCursor(true, header.dataset.id);
            }
        } else if (hoverResizeActive) {
            hoverResizeActive = false;
            setDocumentCursor(false);
        }
    };

    const onPointerLeaveTable = () => {
        if (hoverResizeActive) {
            hoverResizeActive = false;
            setDocumentCursor(false);
        }
    };

    const onPointerDown = (e) => {
        const { nearLeft, nearRight, cell } = getHandlePosition(e);

        if (!nearLeft && !nearRight) return;

        const header = getHeaderForCell(cell, nearLeft);
        if (!header) return;

        document.addEventListener('click', suppressClick, true);
        e.preventDefault();

        activeCol = header;
        activeColSibling = header.nextElementSibling;
        startX = getClientX(e);
        startWidth = Math.floor(activeCol.getBoundingClientRect().width);
        startWidthSibling = activeColSibling ? Math.floor(activeColSibling.getBoundingClientRect().width) : 0;
        currentWidth = startWidth;
        currentWidthSibling = startWidthSibling;
        pointerId = e.pointerId || null;
        startScrollLeft = wrapperEl.scrollLeft;

        const unresizableColumnsWidth = [...tableEl.querySelectorAll('thead th')]
            .filter(({ dataset }) => !dataset.id)
            .reduce((acc, th) => acc + th.getBoundingClientRect().width, 0);
        const calculatedWidthsSum = Object.entries(calculatedWidths).reduce((acc, [id, width]) => {
            if (![activeCol.dataset.id, activeColSibling?.dataset.id].includes(id)) return acc + width;
            return acc;
        }, 0);
        otherColumnsWidth = unresizableColumnsWidth + calculatedWidthsSum;

        setDocumentCursor(true, activeCol.dataset.id);

        try {
            if (pointerId) activeCol.setPointerCapture(pointerId);
        } catch (err) {
            console.error(err);
        }

        attachResizePointerListeners();
    };

    const onPointerMove = (e) => {
        if (!activeCol) return;

        const scrollDelta = wrapperEl.scrollLeft - startScrollLeft;

        const delta = (getClientX(e) - startX) + scrollDelta;
        currentWidth = Math.max(minColumnWidth, Math.round(startWidth + delta));
        const wrapperWidth = wrapperEl.getBoundingClientRect().width - 2;

        if (otherColumnsWidth + currentWidth + currentWidthSibling <= wrapperWidth) {
            // borrow space from the right column
            if (activeColSibling) {
                currentWidthSibling = Math.max(minColumnWidth, Math.round(wrapperWidth - otherColumnsWidth - currentWidth));
                onColumnResize(activeColSibling.dataset.id, currentWidthSibling);
                onColumnResize(activeCol.dataset.id, currentWidth);
            }
        } else {
            onColumnResize(activeCol.dataset.id, currentWidth);
        }
    };

    const onPointerUp = (e) => {
        if (!activeCol) return;

        if (pointerId && typeof e?.pointerId === 'number') {
            try {
                activeCol?.releasePointerCapture(pointerId);
            } catch (err) {
                console.error(err);
            }
        }

        const { header, isOnHandle } = getHandleStatus(e);
        setDocumentCursor(isOnHandle, header?.dataset.id);

        if (!calculatedWidths || typeof calculatedWidths !== 'object') {
            calculatedWidths = {};
        }

        const nextWidths = { ...calculatedWidths };
        nextWidths[activeCol.dataset.id] = currentWidth;
        if (activeColSibling) {
            nextWidths[activeColSibling.dataset.id] = currentWidthSibling;
        }

        calculatedWidths = nextWidths;
        saveColumnWidths(storageKey, nextWidths);

        detachResizePointerListeners();

        activeCol = null;
        activeColSibling = null;
        startX = 0;
        startWidth = 0;
        startWidthSibling = 0;
        currentWidth = 0;
        currentWidthSibling = 0;
        pointerId = null;
        startScrollLeft = 0;
        otherColumnsWidth = 0;
    };

    tableEl.addEventListener('pointermove', onPointerHover, { passive: true });
    tableEl.addEventListener('pointerleave', onPointerLeaveTable);
    tableEl.addEventListener('pointerdown', onPointerDown, true);

    return {
        unmount: () => {
            tableEl.removeEventListener('pointermove', onPointerHover, { passive: true });
            tableEl.removeEventListener('pointerleave', onPointerLeaveTable);
            tableEl.removeEventListener('pointerdown', onPointerDown, true);

            detachResizePointerListeners();

            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        },
        columnsUpdated: (newColumnIds) => {
            calculatedWidths = calculateEqualColumnWidths(newColumnIds);
            onColumnsUpdate(calculatedWidths);
        },
    };
};
